import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  Megaphone,
  Images,
  UserPlus,
  BadgeCheck,
  Loader2,
  Users2,
  Layers,
  ShieldCheck,
} from "lucide-react";
import { fetchActividadFeed } from "../lib/actividad";
import { haceCuanto } from "../lib/conexion";
import { PlanBadge } from "../components/PlanBadge";
import { humanizeAxiosError } from "../lib/api";

const TIPO_META = {
  publicacion: { Icon: Megaphone, color: "#2563EB", tint: "rgba(37,99,235,0.12)" },
  portafolio: { Icon: Images, color: "#16A34A", tint: "rgba(22,163,74,0.12)" },
  miembro: { Icon: UserPlus, color: "#7E22CE", tint: "rgba(126,34,206,0.12)" },
  verificacion: { Icon: BadgeCheck, color: "#059669", tint: "rgba(5,150,105,0.12)" },
};

const RESUMEN_CARDS = [
  { key: "publicaciones", label: "Publicaciones activas", Icon: Megaphone, color: "#2563EB" },
  { key: "miembros", label: "Miembros de la Familia", Icon: Users2, color: "#7E22CE" },
  { key: "trabajos_publicos", label: "Trabajos en vitrinas", Icon: Layers, color: "#16A34A" },
  { key: "verificaciones", label: "Verificaciones", Icon: ShieldCheck, color: "#059669" },
];

export default function ActividadPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const d = await fetchActividadFeed(40);
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setError(humanizeAxiosError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="max-w-[1000px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8" data-testid="actividad-page">
      {/* Cabecera */}
      <header className="mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-brand-navy text-white flex items-center justify-center">
            <Activity size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight">Actividad de la Familia</h1>
            <p className="text-sm text-slate-500">Todo lo que sucede en el Ecosistema ConstruLink en tiempo real.</p>
          </div>
        </div>
      </header>

      {/* Métricas */}
      {data?.resumen && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="actividad-resumen">
          {RESUMEN_CARDS.map((c) => {
            const CIcon = c.Icon;
            return (
              <div key={c.key} className="bg-white rounded-2xl border border-slate-100 p-4 shadow-soft">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-2" style={{ backgroundColor: `${c.color}1a`, color: c.color }}>
                  <CIcon size={18} />
                </div>
                <p className="text-2xl font-extrabold text-slate-900">{data.resumen[c.key] ?? 0}</p>
                <p className="text-xs text-slate-500 font-medium">{c.label}</p>
              </div>
            );
          })}
        </div>
      )}

      {loading && (
        <div className="text-center py-16 text-slate-500 text-sm" data-testid="actividad-loading">
          <Loader2 size={22} className="mx-auto animate-spin" />
          <p className="mt-2">Cargando actividad…</p>
        </div>
      )}

      {error && !loading && (
        <div className="p-4 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm" role="alert">
          {error}
        </div>
      )}

      {/* Feed */}
      {!loading && !error && data && (
        data.eventos.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-12 text-center text-slate-500" data-testid="actividad-vacia">
            <Activity size={28} className="mx-auto text-slate-300" />
            <p className="mt-2 text-sm">Aún no hay actividad. ¡Sé el primero en publicar!</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-soft divide-y divide-slate-100" data-testid="actividad-feed">
            {data.eventos.map((ev) => (
              <FeedRow key={ev.id} ev={ev} />
            ))}
          </div>
        )
      )}
    </div>
  );
}

function FeedRow({ ev }) {
  const meta = TIPO_META[ev.tipo] || TIPO_META.publicacion;
  const Icon = meta.Icon;
  return (
    <Link
      to={ev.enlace}
      className="flex items-start gap-3 p-4 hover:bg-slate-50/60 transition-colors"
      data-testid={`actividad-item-${ev.tipo}`}
    >
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: meta.tint, color: meta.color }}>
        <Icon size={18} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-slate-700 leading-snug">
          <span className="font-bold text-slate-900">{ev.actor_nombre}</span>
          {ev.actor_plan && <span className="ml-1.5 align-middle inline-flex"><PlanBadge plan={ev.actor_plan} /></span>}
          {ev.actor_tipo_label && (
            <span className="ml-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-400">· {ev.actor_tipo_label}</span>
          )}
          <span className="text-slate-500"> {ev.descripcion}</span>
          {ev.titulo && (
            <span className="font-semibold text-brand-blue"> «{ev.titulo}»</span>
          )}
        </p>
        <p className="text-xs text-slate-400 mt-0.5">{haceCuanto(ev.fecha)}</p>
      </div>
      <span className="text-xs font-semibold text-brand-blue whitespace-nowrap self-center hidden sm:inline">
        Ver
      </span>
    </Link>
  );
}
